import React, { useState } from 'react';
import { InterviewPrepResponse, InterviewPrepData } from '@/api/types';
import { Users, Code, HelpCircle, ShieldAlert, BookOpen, Terminal, MessageSquare, RefreshCw } from 'lucide-react';
import { InterviewTabs } from '@/components/ai-tools/interview/InterviewTabs';
import { InterviewContent } from '@/components/ai-tools/interview/InterviewContent';
import { RichTextDisplay } from '@/components/ui/RichTextDisplay';
import { Button } from '@/components/ui/Button';

interface InterviewPrepPanelProps {
  result: InterviewPrepResponse;
  onSave: () => void;
  isSaving: boolean;
  onRetry?: () => void;
}

export const InterviewPrepPanel: React.FC<InterviewPrepPanelProps> = ({ result, onRetry }) => {
  const [activeTab, setActiveTab] = useState<string>('behavioral');

  // Fallback for older saved data that only has markdown
  if (!result.data) {
    return (
      <div className="flex flex-col flex-1 bg-white dark:bg-slate-900 relative rounded-xl border border-slate-200 dark:border-slate-800 h-full overflow-hidden dark:shadow-sm min-h-0">
        <div className="flex items-center justify-between px-4 lg:px-8 py-4 lg:py-5 border-b border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-900 z-10 flex-col sm:flex-row gap-4 sm:gap-2 rounded-t-xl shrink-0">
          <div className="flex items-center gap-2 text-slate-900 dark:text-white">
            <Users className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
            <h2 className="text-sm font-semibold tracking-tight w-full sm:w-auto text-center sm:text-left">Custom Interview Prep Guide</h2>
          </div>
          {onRetry && (
            <Button
              variant="ghost"
              onClick={onRetry}
              className="flex-1 sm:flex-none h-auto py-1.5 px-3 min-h-[36px]"
            >
              <RefreshCw className="w-4 h-4 shrink-0" />
              <span className="whitespace-nowrap ml-1.5 inline">Retry</span>
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-6 pt-6 pb-0 lg:px-8 lg:pt-8 lg:pb-0">
           <RichTextDisplay text={result.markdown} title="" hideDivider={true} />
           <div className="h-6 lg:h-8 shrink-0 w-full" />
        </div>
      </div>
    );
  }

  const data: InterviewPrepData = result.data;

  const tabOptions = [
    { id: 'behavioral', label: 'Behavioral', icon: <MessageSquare className="w-4 h-4 mr-2" /> },
    { id: 'technical', label: 'Technical', icon: <Code className="w-4 h-4 mr-2" /> },
    { id: 'questionsToAsk', label: 'Questions to Ask', icon: <HelpCircle className="w-4 h-4 mr-2" /> },
    { id: 'skillsGap', label: 'Skills Gap', icon: <ShieldAlert className="w-4 h-4 mr-2" /> },
    { id: 'learningPath', label: 'Learning Path', icon: <BookOpen className="w-4 h-4 mr-2" /> },
  ];

  return (
    <div className="flex flex-col flex-1 bg-white dark:bg-slate-900 relative rounded-xl border border-slate-200 dark:border-slate-800 h-full overflow-hidden dark:shadow-sm min-h-0">
      <div className="flex items-center justify-between px-4 lg:px-8 py-4 lg:py-5 border-b border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-900 z-10 flex-col sm:flex-row gap-4 sm:gap-2 rounded-t-xl shrink-0">
        <div className="flex items-center gap-2 text-slate-900 dark:text-white">
          <Terminal className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
          <h2 className="text-sm font-semibold tracking-tight w-full sm:w-auto text-center sm:text-left">Custom Interview Prep Guide</h2>
        </div>
        {onRetry && (
          <Button
            variant="ghost"
            onClick={onRetry}
            className="flex-1 sm:flex-none h-auto py-1.5 px-3 min-h-[36px]"
          >
            <RefreshCw className="w-4 h-4 shrink-0" />
            <span className="whitespace-nowrap ml-1.5 inline">Retry</span>
          </Button>
        )}
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <InterviewTabs 
          tabs={tabOptions} 
          activeTab={activeTab} 
          onChange={setActiveTab} 
        />
        <div className="flex-1 overflow-y-auto px-4 pt-4 pb-0 md:px-6 md:pt-6 lg:px-8 lg:pt-8 lg:pb-0 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 bg-white dark:bg-slate-900">
           <InterviewContent data={data} activeTab={activeTab} />
           <div className="h-4 md:h-6 lg:h-8 shrink-0 w-full" />
        </div>
      </div>
    </div>
  );
};
